import {
	AssetTier,
	BigNum,
	MarketStatus,
	PRICE_PRECISION_EXP,
	SpotMarketConfig,
} from '@drift-labs/sdk';
import { ENUM_UTILS, MarketId } from '@drift/common';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ZERO_PRICE_BIGNUM } from 'src/constants/constants';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import { useWithdrawCollateral } from 'src/hooks/driftActions/spot-management/useWithdrawCollateral';
import useAccountsBeingLiquidated from 'src/hooks/Liquidations/useAccountsBeingLiquidated';
import useAccountTargetSpotBalance from 'src/hooks/useAccountTargetSpotBalance';
import useLazySubAccounts from 'src/hooks/useLazySubAccounts';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useMarketsInfoStore from 'src/stores/useMarketsInfoStore';
import { getCleanedMarketSymbol } from 'src/utils/market';
import { toNotional } from 'src/utils/formatting';
import useMarkPrice from 'src/hooks/useMarkPrice';
import { useApyBreakdownTooltipData } from 'src/hooks/useApyBreakdownTooltipData';
import { PRICE_FALLBACK_DISPLAY_VALUE } from 'src/components/TradeForm/consts';
import useDriftClient from 'src/hooks/useDriftClient';
import useDisplayPrecisionForCollateral from 'src/hooks/useDisplayPrecisionForCollateral';
import { CollateralInputV2 } from '../../Inputs/CollateralInput';
import { InputQuickActions } from '../../Inputs/InputQuickActions';
import Button from '../../primitives/Button/Button';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import MarketIcon from '../../Utils/MarketIcon';
import { getShouldBlockBorrows } from 'src/components/PreLaunchMarketAlerts/PreLaunchMarketAlertUtils';
import useUnsettledPnl from 'src/components/Modals/CollateralModal/hooks/useUnsettledPnl';
import { createSizePresetClickHandler } from 'src/components/Modals/CollateralModal/utils';
import useModal from 'src/components/Modals/hooks/useModal';
import useCollateralModalState from 'src/components/Modals/CollateralModal/hooks/useCollateralModalState';
import { CollateralModalProps } from 'src/components/Modals/CollateralModal/types';
import BaseModal from 'src/components/primitives/BaseModal/BaseModal';
import InlineAlert from 'src/components/primitives/InlineAlert/InlineAlert';
import TextButton from 'src/components/primitives/TextButton/TextButton';
import SubAccountSelector from './components/SubAccountSelector';

const BorrowUnsettledAmountMessage = () => {
	const { hasUnsettled, unsettledNumber, handleSettleNow } = useUnsettledPnl();

	if (!hasUnsettled) return null;

	return (
		<div className="flex w-full items-start justify-start gap-1">
			<span>
				You have an unsettled amount of {toNotional(unsettledNumber)}.
			</span>
			<TextButton onClick={handleSettleNow} variant="tertiary" size="sm">
				Settle now
			</TextButton>
		</div>
	);
};

const BorrowLimitsDisplay = ({
	borrowLimitBigNum,
	onAmountSelect,
	onMaxSelect,
}: {
	borrowLimitBigNum: BigNum;
	onAmountSelect: (amount: string) => void;
	onMaxSelect: () => void;
}) => {
	return (
		<div className="flex w-full items-start justify-between">
			<span>Borrow Limit</span>
			<InputQuickActions.SizePresets
				onClick={createSizePresetClickHandler({
					maxAmount: borrowLimitBigNum,
					onMaxSelect,
					onAmountSelect,
					format: 'printShort',
				})}
				maxAmount={borrowLimitBigNum}
			/>
		</div>
	);
};

const BorrowAPYDisplay = ({
	selectedCollatBank,
	borrowAPYBigNum,
}: {
	selectedCollatBank: SpotMarketConfig;
	borrowAPYBigNum: BigNum;
}) => {
	return (
		<div className="flex w-full items-center justify-between">
			<span>Borrow APY</span>
			<div className="flex items-center gap-1">
				<MarketIcon
					marketSymbol={getCleanedMarketSymbol(selectedCollatBank.symbol)}
					className="h-4 w-4"
				/>
				<span className="text-text-default">
					{getCleanedMarketSymbol(selectedCollatBank.symbol)}
				</span>
				<span className="text-star-yellow">{borrowAPYBigNum.toFixed(2)}%</span>
			</div>
		</div>
	);
};

const BalanceChanges = ({
	totalBorrowedAmount,
	afterLiqPriceBigNum,
	selectedCollatBank,
}: {
	totalBorrowedAmount: BigNum;
	afterLiqPriceBigNum: BigNum;
	selectedCollatBank: SpotMarketConfig;
}) => {
	return (
		<div className="flex flex-col gap-1">
			<div className="flex items-center justify-between gap-1">
				<span>Total Borrowed</span>
				<span className="text-text-default">
					{`${totalBorrowedAmount.prettyPrint()} ${getCleanedMarketSymbol(
						selectedCollatBank.symbol
					)}`}
				</span>
			</div>
			<div className="flex items-center justify-between gap-1">
				<span>Liquidation Price</span>
				<span className="text-text-default">
					{afterLiqPriceBigNum.lte(BigNum.zero(PRICE_PRECISION_EXP))
						? PRICE_FALLBACK_DISPLAY_VALUE
						: `${toNotional(afterLiqPriceBigNum.toNum())}`}
				</span>
			</div>
		</div>
	);
};

const BorrowModal = ({
	collateralIndex,
	collateralPoolId,
	targetAccountKey,
	defaultLargestBalance,
}: CollateralModalProps) => {
	const { withdrawCollateral } = useWithdrawCollateral();
	const borrowData = useDriftStore((s) => s.borrowLendData);
	const availableSubaccounts = useLazySubAccounts();
	const accountsBeingLiquidated = useAccountsBeingLiquidated();
	const { captureEvent } = usePostHogCapture();
	const isMobile = useIsMobileScreenSize();
	const driftClient = useDriftClient();
	const { hide } = useModal();

	const {
		updateCollateralIndex,
		handleTargetAccountChange,
		selectedCollateralMarket,
		selectedTargetAccountKey,
		selectedTargetAccount,
		selectedPoolId,
	} = useCollateralModalState({
		collateralIndex,
		collateralPoolId,
		targetAccountKey,
		defaultLargestBalance,
	});

	const [submitting, setSubmitting] = useState(false);
	const [collatTransferAmount, setCollatTransferAmount] = useState('');
	const [isMax, setIsMax] = useState(false);

	const getMarketInfo = useMarketsInfoStore()?.getMarketInfoByIndexAndType;

	const apyData = useApyBreakdownTooltipData(
		selectedCollateralMarket.marketIndex
	);
	const borrowApyBigNum = apyData.borrowing.totalApyBN;

	// Calculate liquidation prices
	const currentLiqPriceBigNum = useMemo(() => {
		return selectedTargetAccount?.client
			? BigNum.from(
					selectedTargetAccount.client.spotLiquidationPrice(
						selectedCollateralMarket.marketIndex
					),
					PRICE_PRECISION_EXP
				)
			: ZERO_PRICE_BIGNUM;
	}, [selectedTargetAccount, selectedCollateralMarket.marketIndex]);

	const [afterLiqPriceBigNum, setAfterLiqPriceBigNum] = useState(
		currentLiqPriceBigNum
	);

	// Get borrow limits and balances - use false for reduceOnly to enable borrowing
	const targetAccountBankBalance = useAccountTargetSpotBalance(
		selectedCollateralMarket,
		selectedTargetAccountKey,
		false
	);

	const _collateralDisplayPrecision = useDisplayPrecisionForCollateral(
		selectedCollateralMarket.marketIndex
	);

	const marketId = MarketId.createSpotMarket(
		selectedCollateralMarket.marketIndex
	);

	const spotMarketAccount = driftClient?.getSpotMarketAccount(
		marketId.marketIndex
	);

	const _markPrice = useMarkPrice(marketId);

	const collatTransferAmountBignum = BigNum.fromPrint(
		collatTransferAmount ? collatTransferAmount : '0',
		selectedCollateralMarket.precisionExp
	);

	const borrowLimitBigNum =
		targetAccountBankBalance.withdrawLimitBase || BigNum.zero();

	const bufferedBorrowMax = useMemo(() => {
		return (targetAccountBankBalance.withdrawLimitBase || BigNum.zero()).scale(
			99,
			100
		);
	}, [targetAccountBankBalance.withdrawLimitBase?.toString()]);

	// Check if borrows are disabled
	const shouldBlockBorrows = !selectedTargetAccount?.client
		? true
		: getShouldBlockBorrows(
				selectedTargetAccount.client.getUserAccount(),
				getMarketInfo
			) && selectedCollateralMarket.marketIndex !== 0;

	const marketReduceOnly = ENUM_UTILS.match(
		spotMarketAccount?.status,
		MarketStatus.REDUCE_ONLY
	);

	const marketProtected = ENUM_UTILS.match(
		spotMarketAccount?.assetTier,
		AssetTier.PROTECTED
	);

	// Get current total borrowed amount
	const totalBorrowedAmount = useMemo(() => {
		return targetAccountBankBalance.liabilityBaseBalance || BigNum.zero();
	}, [targetAccountBankBalance]);

	const selectedAccountCanbeLiqd = accountsBeingLiquidated.find(
		(account) => account.userKey === selectedTargetAccountKey
	);

	const isPureWithdrawal =
		collatTransferAmountBignum.gtZero() &&
		targetAccountBankBalance.netBaseBalance.gte(collatTransferAmountBignum);

	const isPartialWithdrawal =
		collatTransferAmountBignum.gtZero() &&
		targetAccountBankBalance.netBaseBalance.gtZero() &&
		collatTransferAmountBignum.gt(targetAccountBankBalance.netBaseBalance);

	const allowConfirmation =
		(!marketProtected || isPureWithdrawal) &&
		(!marketReduceOnly || isPureWithdrawal) &&
		(!shouldBlockBorrows || isPureWithdrawal) &&
		!submitting &&
		collatTransferAmount &&
		collatTransferAmountBignum.gt(
			BigNum.zero(selectedCollateralMarket.precisionExp),
			true
		) &&
		!selectedAccountCanbeLiqd;

	const handleBorrow = useCallback(async () => {
		setSubmitting(true);

		const collateralAmount = BigNum.fromPrint(
			collatTransferAmount,
			selectedCollateralMarket.precisionExp
		);

		const createsBorrowLiability =
			targetAccountBankBalance.assetBaseBalance.lt(collateralAmount);

		const result = await withdrawCollateral({
			amount: collateralAmount,
			spotMarketConfig: selectedCollateralMarket,
			userAccount: selectedTargetAccount?.client?.getUserAccount() ?? undefined,
			isBorrow: createsBorrowLiability,
		});

		setSubmitting(false);

		captureEvent('submitted_borrow', {
			spot_market_symbol: selectedCollateralMarket.symbol,
			borrow_amount: collatTransferAmount,
		});

		if (result === true) {
			hide();
		}
	}, [
		collatTransferAmount,
		selectedCollateralMarket,
		targetAccountBankBalance.assetBaseBalance,
		selectedTargetAccount,
		selectedTargetAccountKey,
		captureEvent,
		hide,
	]);

	const handleValueChange = (newValue?: string) => {
		if (newValue !== undefined) {
			setIsMax(false);

			// Set input to max amount if exceeds limit
			if (newValue === '') {
				setCollatTransferAmount(newValue);
				return;
			}

			const inputBigNum = BigNum.fromPrint(
				newValue,
				selectedCollateralMarket.precisionExp
			);
			if (inputBigNum.lte(targetAccountBankBalance.withdrawLimitBase)) {
				setCollatTransferAmount(newValue);
			} else {
				// Set to max amount if input exceeds limit
				setCollatTransferAmount(
					targetAccountBankBalance.withdrawLimitBase.printShort()
				);
				setIsMax(true);
			}
		}
	};

	const handleMax = () => {
		setCollatTransferAmount(bufferedBorrowMax.printShort());
		setIsMax(true);
	};

	// Update liquidation price when amount changes
	useEffect(() => {
		if (spotMarketAccount) {
			if (selectedTargetAccount?.client) {
				const afterLiqPrice = BigNum.from(
					selectedTargetAccount.client.spotLiquidationPrice(
						spotMarketAccount.marketIndex,
						collatTransferAmountBignum?.neg()?.val
					),
					PRICE_PRECISION_EXP
				);

				setAfterLiqPriceBigNum(afterLiqPrice);
			}
		}
	}, [
		collatTransferAmountBignum?.toString(),
		spotMarketAccount?.marketIndex,
		selectedTargetAccount?.userKey,
	]);

	// If max is selected, keep input value in sync with max. allowed value
	useEffect(() => {
		if (isMax) {
			setCollatTransferAmount(bufferedBorrowMax.printShort());
		}
	}, [isMax, bufferedBorrowMax.printShort()]);

	// Reset transfer amount if market selection changes
	useEffect(() => {
		setCollatTransferAmount('');
	}, [selectedCollateralMarket?.symbol]);

	const filterOutMarketIndices = useMemo(() => {
		return borrowData
			.filter((data) =>
				ENUM_UTILS.match(data.spotMarketAccount.assetTier, AssetTier.PROTECTED)
			) // Borrow disabled for protected assets
			.map((data) => data.bankIndex);
	}, [borrowData]);

	return (
		<BaseModal.Container onClose={hide} dataTestId="borrow-modal">
			<BaseModal.Title onClose={hide} closeButtonTestId="borrow-modal-close">
				Borrow
			</BaseModal.Title>
			<BaseModal.Body>
				<span className="text-text-default">
					Borrowed amounts are withdrawn to your wallet.
				</span>

				{availableSubaccounts.length > 1 && (
					<SubAccountSelector
						mode="withdraw"
						accountLabel="Account"
						selectedTargetAccountKey={selectedTargetAccountKey}
						selectedPoolId={selectedPoolId}
						handleTargetAccountChange={handleTargetAccountChange}
					/>
				)}

				<div className="flex flex-col gap-2">
					<CollateralInputV2
						maxAmount={targetAccountBankBalance.withdrawLimitBase}
						selectedMarket={selectedCollateralMarket}
						onChangeValue={handleValueChange}
						onChangeMarket={(val) => updateCollateralIndex(val.marketIndex)}
						value={
							isMax
								? targetAccountBankBalance.withdrawLimitBase.printShort()
								: collatTransferAmount
						}
						label="Assets and amount"
						source={{
							type: 'default',
							filterOutMarketIndices,
						}}
						poolId={selectedPoolId}
						disabled={marketProtected}
						fullScreenPopover={isMobile}
						fullScreenPopoverTitle="Select Asset"
					/>
					{marketReduceOnly && (
						<InlineAlert
							variant="warning"
							title="Borrows are currently disabled on this market."
						/>
					)}

					{!marketReduceOnly && (
						<>
							<BorrowLimitsDisplay
								borrowLimitBigNum={borrowLimitBigNum}
								onAmountSelect={(val) => {
									setIsMax(false);
									setCollatTransferAmount(val);
								}}
								onMaxSelect={handleMax}
							/>

							<BorrowAPYDisplay
								selectedCollatBank={selectedCollateralMarket}
								borrowAPYBigNum={borrowApyBigNum}
							/>
						</>
					)}
				</div>

				{shouldBlockBorrows && (
					<InlineAlert
						variant="error"
						title="Non-USDC Borrows are disabled for this account"
					>
						As a protocol safety measure, non-USDC borrows are disabled when you
						have an open position in a Pre-Launch Market. To open a new borrow,
						use a separate subaccount or close your open perpetuals position.
					</InlineAlert>
				)}

				<BorrowUnsettledAmountMessage />

				{selectedAccountCanbeLiqd && (
					<InlineAlert
						variant="error"
						title="Withdrawal is disabled during liquidation. Please try again later."
					/>
				)}

				{isPureWithdrawal && (
					<span>
						You already have{' '}
						<span className="text-text-default">
							{targetAccountBankBalance.netBaseBalance.prettyPrint()}{' '}
							{getCleanedMarketSymbol(selectedCollateralMarket.symbol)}
						</span>{' '}
						deposits. We&apos;ll withdraw{' '}
						<span className="text-text-default">
							{collatTransferAmountBignum.prettyPrint()}{' '}
							{getCleanedMarketSymbol(selectedCollateralMarket.symbol)}
						</span>{' '}
						to your wallet, leaving a net deposit of{' '}
						<span className="text-text-default">
							{targetAccountBankBalance.netBaseBalance
								.sub(collatTransferAmountBignum)
								.prettyPrint()}{' '}
							{getCleanedMarketSymbol(selectedCollateralMarket.symbol)}
						</span>
						.
					</span>
				)}

				{isPartialWithdrawal && (
					<span>
						You already have{' '}
						<span className="text-text-default">
							{targetAccountBankBalance.netBaseBalance.prettyPrint()}{' '}
							{getCleanedMarketSymbol(selectedCollateralMarket.symbol)}
						</span>{' '}
						deposits. We&apos;ll use that first, so your net borrow will be{' '}
						<span className="text-text-default">
							{collatTransferAmountBignum
								.sub(targetAccountBankBalance.netBaseBalance)
								.prettyPrint()}{' '}
							{getCleanedMarketSymbol(selectedCollateralMarket.symbol)}
						</span>
						.
					</span>
				)}

				<BalanceChanges
					totalBorrowedAmount={totalBorrowedAmount.add(
						collatTransferAmountBignum
					)}
					afterLiqPriceBigNum={afterLiqPriceBigNum}
					selectedCollatBank={selectedCollateralMarket}
				/>
			</BaseModal.Body>
			<BaseModal.Footer>
				<Button
					className="w-full"
					size="lg"
					onClick={handleBorrow}
					disabled={!allowConfirmation}
					isLoading={submitting}
					dataTestId="borrow-modal-submit"
				>
					Confirm {isPureWithdrawal ? 'Withdrawal' : 'Borrow'}
				</Button>
			</BaseModal.Footer>
		</BaseModal.Container>
	);
};

export default React.memo(BorrowModal);
